import CodeBlock from '../CodeBlock';

function FnRow({ name, sig, desc }: { name: string; sig: string; desc: string }) {
  return (
    <tr className="border-b border-gray-800/50 last:border-0">
      <td className="py-2 pr-4 font-mono text-sky-400 whitespace-nowrap">{name}</td>
      <td className="py-2 pr-4 font-mono text-gray-500 text-xs whitespace-nowrap">{sig}</td>
      <td className="py-2 text-gray-400">{desc}</td>
    </tr>
  );
}

export default function DocsFactorsModule() {
  return (
    <section data-docs-section="factors" className="text-left mb-16">
      <h2 className="text-2xl font-bold text-white mb-2 group">
        <a href="#factors" className="hover:text-sky-400 transition-colors">pa.factors</a>
        <span className="ml-2 opacity-0 group-hover:opacity-100 text-gray-600 transition-opacity">#</span>
      </h2>
      <p className="text-gray-400 mb-6">
        Pure functions over return series — no state, no side effects. Every function
        accepts a <code className="text-gray-300">pd.Series</code> of simple period returns and
        produces a scalar or derived series.
      </p>

      {/* Performance */}
      <div id="factors-performance" data-docs-section="factors-performance" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-3">Performance</h3>
        <div className="overflow-x-auto">
          <table className="text-sm text-left w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="py-2 pr-4 text-gray-400 font-medium">Function</th>
                <th className="py-2 pr-4 text-gray-400 font-medium">Signature</th>
                <th className="py-2 text-gray-400 font-medium">Description</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              <FnRow name="sharpe" sig="(returns, rf=0.0) → float" desc="Annualized Sharpe ratio. rf is annualized risk-free rate." />
              <FnRow name="sortino" sig="(returns, rf=0.0) → float" desc="Annualized Sortino ratio (downside deviation only)." />
              <FnRow name="calmar" sig="(returns) → float" desc="Calmar ratio: CAGR / |max drawdown|." />
              <FnRow name="total_return" sig="(returns) → float" desc="Total cumulative return as decimal (0.10 = 10%)." />
              <FnRow name="annualized_return" sig="(returns) → float" desc="Compound annual growth rate (CAGR)." />
              <FnRow name="win_rate" sig="(returns) → float" desc="Fraction of positive-return periods [0, 1]." />
              <FnRow name="profit_factor" sig="(returns) → float" desc="Gross profits / gross losses (>1 = profitable)." />
            </tbody>
          </table>
        </div>
        <CodeBlock
          code={`from propalgos.factors import sharpe, sortino, total_return

ret = result.returns
print(f"Sharpe:  {sharpe(ret):.2f}")
print(f"Sortino: {sortino(ret):.2f}")
print(f"Total:   {total_return(ret):.1%}")`}
        />
      </div>

      {/* Risk */}
      <div id="factors-risk" data-docs-section="factors-risk" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-3">Risk</h3>
        <div className="overflow-x-auto">
          <table className="text-sm text-left w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="py-2 pr-4 text-gray-400 font-medium">Function</th>
                <th className="py-2 pr-4 text-gray-400 font-medium">Signature</th>
                <th className="py-2 text-gray-400 font-medium">Description</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              <FnRow name="max_drawdown" sig="(returns) → float" desc="Max peak-to-trough drawdown (negative value, e.g. -0.25 = 25%)." />
              <FnRow name="drawdown_series" sig="(returns) → Series" desc="Full drawdown time series at each point." />
              <FnRow name="rolling_volatility" sig="(returns, window=21) → Series" desc="Rolling annualized volatility. Default window ~1 month." />
              <FnRow name="annualized_volatility" sig="(returns) → float" desc="Annualized standard deviation of returns." />
              <FnRow name="var" sig="(returns, confidence=0.95) → float" desc="Historical Value at Risk at given confidence level." />
              <FnRow name="cvar" sig="(returns, confidence=0.95) → float" desc="Conditional VaR (Expected Shortfall) — avg loss in worst tail." />
              <FnRow name="beta" sig="(returns, benchmark) ��� float" desc="Beta relative to a benchmark return series." />
            </tbody>
          </table>
        </div>
        <CodeBlock
          code={`from propalgos.factors import max_drawdown, var, cvar, rolling_volatility

ret = result.returns
print(f"Max DD:  {max_drawdown(ret):.1%}")
print(f"VaR 95%: {var(ret, 0.95):.2%}")
print(f"CVaR:    {cvar(ret, 0.95):.2%}")

# Rolling vol for time-varying risk analysis
vol = rolling_volatility(ret, window=21)  # → Series`}
        />
      </div>
    </section>
  );
}
